import { getLockSpec } from 'redux/middleware/api/embed-configuration-service';
import { getPodcastWorkflowTemplate } from 'redux/middleware/api/podcast-service/actions';
import {
  podcastWorkflowTemplateSelector,
  templateLockSpecUuidSelector,
} from 'redux/modules/entities';
import { ThunkAction } from 'redux/types';
import { TemplateType } from 'types';
import { loadVideosForConfigs } from './embed-config';

export const loadTemplateLockSpec = (
  templateId: string,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  const lockSpecUuid = templateLockSpecUuidSelector(templateId)(getState());

  if (lockSpecUuid) {
    await dispatch(getLockSpec({ lockSpecId: lockSpecUuid }));
  }
};

/**
 * Loads the entities needed to edit a specific template that is already in
 * the entity store: its lockSpec and the video uploads referenced by its
 * preview configuration (with variations, since the template will be rendered
 * in an editing context). Call this when a template is selected for editing —
 * never for a list of templates the user is only browsing.
 */
export const loadTemplateEditingDependencies = (
  templateId: string,
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  const template = podcastWorkflowTemplateSelector(templateId)(getState());

  await Promise.all([
    dispatch(loadTemplateLockSpec(templateId)),
    dispatch(
      loadVideosForConfigs(template?.previewConfiguration, {
        includeVariations: true,
      }),
    ),
  ]);
};

/**
 * Fetches a podcast workflow template and the entities needed to edit it
 * (lockSpec, referenced video uploads). Used by flows that load a single
 * template directly rather than selecting one from an already-loaded list.
 */
export const loadPodcastWorkflowTemplate = (
  templateId: string,
  templateType: TemplateType,
): ThunkAction<Promise<void>> => async dispatch => {
  await dispatch(getPodcastWorkflowTemplate(templateId, templateType));
  await dispatch(loadTemplateEditingDependencies(templateId));
};
